import { API_ORIGIN } from './constants';
import { DEFAULT_AVATAR_SRC, isPlaceholderAvatar } from './defaultAvatar';

const MEDIA_PREFIX = '/api/v1/images';

/** Собрать URL картинки через API (обход 403 на /images/ у nginx). */
function resolveMediaUrl(type, path) {
  if (!path) return '';
  if (path.startsWith('data:')) return path;

  if (path.startsWith(`${MEDIA_PREFIX}/`)) {
    return `${API_ORIGIN}${path}`;
  }

  if (path.startsWith('http')) {
    const legacy = path.match(new RegExp(`/images/${type}/([^?#]+)`));
    if (legacy) {
      return `${API_ORIGIN}${MEDIA_PREFIX}/${type}/${legacy[1]}`;
    }
    const apiLegacy = path.match(new RegExp(`${MEDIA_PREFIX}/${type}/([^?#]+)`));
    if (apiLegacy) {
      try {
        const { pathname } = new URL(path);
        return `${API_ORIGIN}${pathname}`;
      } catch {
        return path;
      }
    }
    return path;
  }

  const clean = path.replace(/^\/+/, '');
  if (clean.startsWith(`api/v1/images/${type}/`)) {
    return `${API_ORIGIN}/${clean}`;
  }
  if (clean.startsWith(`images/${type}/`)) {
    const name = clean.split('/').pop();
    return `${API_ORIGIN}${MEDIA_PREFIX}/${type}/${name}`;
  }

  const name = clean.split('/').pop();
  return name ? `${API_ORIGIN}${MEDIA_PREFIX}/${type}/${name}` : '';
}

/** URL картинки категории */
export function categoryImageUrl(path) {
  if (!path) return '';
  return resolveMediaUrl('category', path);
}

/** Запасной URL (другое расширение) если основной файл не найден. */
export function categoryImageFallbackUrl(path) {
  const primary = categoryImageUrl(path);
  if (!primary) return '';

  if (/\.webp($|\?)/i.test(primary)) {
    return primary.replace(/\.webp($|\?)/i, '.jpg$1');
  }
  if (/\.jpe?g($|\?)/i.test(primary)) {
    return primary.replace(/\.jpe?g($|\?)/i, '.webp$1');
  }
  if (/\.png($|\?)/i.test(primary)) {
    return primary.replace(/\.png($|\?)/i, '.webp$1');
  }

  return '';
}

/**
 * URL для картинок с API (товары, категории).
 * В БД хранятся имена файлов или полные URL.
 */
export function assetUrl(path) {
  if (!path) return '';
  if (path.startsWith('data:') || path.startsWith('http')) {
    if (path.startsWith('http')) {
      for (const type of ['product', 'category', 'poster']) {
        const resolved = resolveMediaUrl(type, path);
        if (resolved !== path) return resolved;
      }
    }
    return path;
  }
  if (path.startsWith(`${MEDIA_PREFIX}/`)) {
    return `${API_ORIGIN}${path}`;
  }
  const clean = path.replace(/^\/+/, '');
  if (clean.startsWith('images/product/')) {
    return resolveMediaUrl('product', path);
  }
  if (clean.startsWith('images/category/')) {
    return resolveMediaUrl('category', path);
  }
  if (clean.startsWith('images/poster/')) {
    return resolveMediaUrl('poster', path);
  }
  return resolveMediaUrl('product', path);
}

/** Аватар пользователя (пустая строка = нет загруженного фото). */
export function avatarUrl(path) {
  if (!path || isPlaceholderAvatar(path)) return '';
  return resolveMediaUrl('avatar', path);
}

/** Запасной URL (другое расширение) если основной файл не найден. */
export function avatarImageFallbackUrl(path) {
  const primary = avatarUrl(path);
  if (!primary) return '';

  if (/\.webp($|\?)/i.test(primary)) {
    return primary.replace(/\.webp($|\?)/i, '.jpg$1');
  }
  if (/\.jpe?g($|\?)/i.test(primary)) {
    return primary.replace(/\.jpe?g($|\?)/i, '.webp$1');
  }
  if (/\.png($|\?)/i.test(primary)) {
    return primary.replace(/\.png($|\?)/i, '.webp$1');
  }

  return '';
}

/** URL для отображения в <img> — всегда валидный src. */
export function avatarDisplayUrl(path) {
  return avatarUrl(path) || DEFAULT_AVATAR_SRC;
}

/** Баннер главной страницы (файл в images/poster/) */
export function posterUrl(filename) {
  if (!filename) return '';
  if (filename.startsWith('http') || filename.startsWith('data:')) {
    return resolveMediaUrl('poster', filename);
  }
  return resolveMediaUrl('poster', filename);
}
